import commons from "./commons";

/*
 * SS05783 — opening an entry link from a notification / mention email.
 *
 * Those links are workspace-agnostic by construction: NotificationMessageGenerator builds
 * `/#/?id=<module>_<entry>` (and `/#/list/<module>?l=<listing>&id=<module>_<entry>` when the
 * template names a listing), so the id carries a module and an entry and nothing else. The
 * client therefore opens them under whatever workspace happened to be selected last.
 *
 * The server does not treat those two workspaces the same way. The workspace gate
 * (Authorizer.Access) passes for a module in ANY workspace the user belongs to, but module
 * rights come from CurrentAuthorization, whose profile details are resolved only for profile
 * modules of the wsId on the request. A user whose read right on the module comes from a
 * profile listing (authorization entityType 6) in the entry's own workspace therefore fails
 * the module gate and gets "You do not have access to this detail" — while the very same link
 * works once they switch to that workspace by hand.
 *
 * So the client resolves the owning workspace itself before the entry is opened. Extracted from
 * Client.vue so the decision can be tested without booting the app.
 */
export default {
  methods: {
    /**
     * The module a deep link points at, or null when the route is not one.
     * @returns {string|number|null}
     */
    deepLinkModuleId() {
      const queryId = this.$route && this.$route.query && this.$route.query.id;
      if (!queryId) {
        return null;
      }

      /* deprecated entry-only links (prefix e/f) decode without a module — nothing to resolve */
      const decoded = commons.queryDecodeids(queryId) || {};
      return decoded.moduleId || null;
    },

    /**
     * The workspace bootstrap returns only the modules readable in that workspace (published,
     * and filtered by BasicRights.Read), so a module missing here is exactly the case the
     * server would refuse — whether it belongs to another workspace or is unreadable in this one.
     */
    workspaceHasModule(moduleId) {
      return (this.workspaceModules || []).some(x => x.id == moduleId);
    },

    /**
     * Switch to the workspace that owns the deep-linked module, if it is not this one.
     */
    async switchWorkspaceForDeepLink(moduleId) {
      /* Only switch on a module the loaded workspace genuinely does not carry: the server
         resolves a shared module to its OWNER workspace, so switching on one we already have
         would move the user out of the workspace they were browsing. */
      if (!moduleId || this.workspaceHasModule(moduleId)) {
        return false;
      }

      try {
        /* isInitial keeps the failure quiet: a module the user cannot reach from any workspace
           falls back to the default workspace, and the entry request then reports the real
           denial rather than a second, misleading "couldn't load the workspace" toast. */
        await this.loadWorkspace({ moduleId: moduleId, client: true }, { isInitial: true });
        return true;
      } catch (err) {
        console.error("Failed to resolve the workspace for this link", err);
        return false;
      }
    },
  },
};
